import React from 'react';
import { Sparkles, Trash2, Plus } from 'lucide-react';

function descToLines(text) {
  return String(text || '')
    .split(/\n|(?<=[.;])\s+/)
    .map(s => s.trim())
    .filter(Boolean);
}

/**
 * TestAttributesGenerator — presentational. Generation state + action buttons
 * live in TestDesignTab (on the section header). This renders one of:
 *   idle       → "Generate" empty state
 *   generating → spinner + progress
 *   generated  → editable TEST ATTRIBUTES table (one test step = one attribute row)
 *
 * Props:
 *   testSteps, onStepChange, onReplaceSteps
 *   phase ('idle'|'generating'|'generated'), progress[], error, onGenerate
 */
export default function TestAttributesGenerator({
  testSteps,
  onStepChange,
  onReplaceSteps,
  phase,
  progress = [],
  error = '',
  onGenerate,
}) {
  const rows = (testSteps || []).map((step, idx) => ({
    ref: String.fromCharCode(65 + idx),
    stepIndex: idx,
    description: step._text ?? step.summary ?? '',
    procedures: Array.isArray(step.key_points) ? step.key_points : descToLines(step.description),
  }));

  /* ---- Edit helpers ---- */
  const handleDescChange = (stepIdx, value) => {
    const step = { ...(testSteps[stepIdx] || {}), _text: value, summary: value, description: value };
    onStepChange(stepIdx, step);
  };
  const handleProceduresChange = (stepIdx, lines) => {
    const step = { ...(testSteps[stepIdx] || {}), key_points: lines };
    onStepChange(stepIdx, step);
  };
  const handleDeleteRow = (stepIdx) => {
    const remaining = (testSteps || [])
      .filter((_, i) => i !== stepIdx)
      .map((s, i) => ({ ...s, step_number: i + 1 }));
    onReplaceSteps?.(remaining);
  };
  const handleAddRow = () => {
    const next = [
      ...(testSteps || []),
      { _text: '', summary: '', description: '', key_points: [], test_attributes: [] },
    ].map((s, i) => ({ ...s, step_number: i + 1 }));
    onReplaceSteps?.(next);
  };

  /* ---- idle ---- */
  if (phase === 'idle') {
    return (
      <div className="p-8 text-center">
        <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="h-7 w-7 text-blue-600" />
        </div>
        <p className="text-sm text-gray-600 mb-4">
          No test attributes or procedures defined yet.
          Generate them using AI based on this control's description.
        </p>
        {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
        <button
          type="button"
          onClick={onGenerate}
          className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
        >
          <Sparkles className="h-4 w-4" />
          Generate Test Attributes &amp; Procedures
        </button>
      </div>
    );
  }

  /* ---- generating ---- */
  if (phase === 'generating') {
    return (
      <div className="p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium text-gray-700">
            {progress[progress.length - 1] || 'Generating…'}
          </span>
        </div>
        <ul className="space-y-1 text-xs text-gray-500">
          {progress.map((msg, i) => <li key={i}>• {msg}</li>)}
        </ul>
      </div>
    );
  }

  /* ---- generated (editable table) ---- */
  return (
    <div>
      {error && <p className="text-sm text-red-500 px-6 pt-3">{error}</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              <th className="px-6 py-3 text-center w-28">Attribute Reference</th>
              <th className="px-6 py-3 text-left w-80">Attribute Description</th>
              <th className="px-6 py-3 text-left">Test Steps</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-400">
                  No attributes. Add one below or regenerate.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.stepIndex} className="align-top hover:bg-gray-50 transition-colors group">
                <td className="px-6 py-4 text-center bg-blue-50/40">
                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                    {row.ref}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <textarea
                    rows={4}
                    value={row.description}
                    onChange={e => handleDescChange(row.stepIndex, e.target.value)}
                    placeholder="What is being tested…"
                    className="w-full text-sm text-gray-800 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none resize-y bg-transparent px-0 py-0.5"
                  />
                </td>
                <td className="px-6 py-4">
                  <ProcedureEditor
                    lines={row.procedures}
                    onChange={lines => handleProceduresChange(row.stepIndex, lines)}
                  />
                </td>
                <td className="px-4 py-4">
                  <button
                    type="button"
                    onClick={() => handleDeleteRow(row.stepIndex)}
                    className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove attribute"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-3 border-t border-gray-100">
        <button
          type="button"
          onClick={handleAddRow}
          className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
        >
          <Plus className="h-3 w-3" /> Add Attribute
        </button>
      </div>
    </div>
  );
}

/** Editable numbered procedure list (array of strings). */
function ProcedureEditor({ lines, onChange }) {
  const items = Array.isArray(lines) ? lines : [];
  const updateLine = (idx, text) => onChange(items.map((l, i) => (i === idx ? text : l)));
  const addLine = () => onChange([...items, '']);
  const removeLine = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-1">
      {items.map((line, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <span className="text-blue-600 text-xs font-medium mt-1.5 w-5 shrink-0">{idx + 1}.</span>
          <input
            type="text"
            value={line.replace(/^\s*\d+\.\s*/, '')}
            onChange={e => updateLine(idx, e.target.value)}
            placeholder="Describe the procedure step…"
            className="flex-1 text-sm text-gray-700 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent py-0.5"
          />
          <button type="button" onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-400 mt-1" title="Remove step">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-gray-400 italic">No procedure steps yet.</p>}
      <button type="button" onClick={addLine} className="text-xs text-blue-500 hover:text-blue-700 inline-flex items-center gap-1 mt-1">
        <Plus className="h-3 w-3" /> Add step
      </button>
    </div>
  );
}
